import React, { useState, useEffect } from 'react';
import DatabaseService from '../services/DatabaseService';
import apiService from '../services/ApiService';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('SaleDetailsModal Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">เกิดข้อผิดพลาด</h5>
                <button type="button" className="btn-close" onClick={this.props.onClose}></button>
              </div>
              <div className="modal-body text-center">
                <div className="alert alert-danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  เกิดข้อผิดพลาดในการแสดงรายละเอียด กรุณาลองใหม่อีกครั้ง
                </div>
                <button className="btn btn-primary" onClick={() => window.location.reload()}>
                  รีเฟรชหน้า
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const SaleDetailsModal = ({ sale, isOpen, onClose, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    customer_phone: '',
    additional_phone: ''
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [pmStats, setPmStats] = useState({});
  const [isInitializingPM, setIsInitializingPM] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // อัปเดต editData เมื่อ sale เปลี่ยนแปลง
  useEffect(() => {
    if (sale) {
      setEditData({
        customer_phone: sale.customer_phone || '',
        additional_phone: sale.additional_phone || ''
      });
    }
  }, [sale]);

  // ดึงข้อมูล PM statistics สำหรับแต่ละ serial number
  useEffect(() => {
    const fetchPmStats = async () => {
      if (sale && sale.items && Array.isArray(sale.items)) {
        const stats = {};
        for (const item of sale.items) {
          if (item.serial_number && item.pm_service_count > 0) {
            try {
              const response = await fetch(`/api/pm-services/stats/serial/${item.serial_number}`);
              if (response.ok) {
                const data = await response.json();
                stats[item.serial_number] = data;
              }
            } catch (error) {
              console.error(`Error fetching PM stats for ${item.serial_number}:`, error);
            }
          }
        }
        setPmStats(stats);
      }
    };

    if (isOpen && sale) {
      fetchPmStats();
    }
  }, [sale, isOpen]);

  // ฟังก์ชันบันทึกเบอร์โทร
  const handleSavePhones = async () => {
    setIsUpdating(true);
    try {
      const response = await fetch(`/api/sales/${sale.id}/update-phones`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customer_phone: editData.customer_phone,
          additional_phone: editData.additional_phone
        })
      });

      if (response.ok) {
        setIsEditing(false);
        if (onUpdate) onUpdate();
        alert('อัปเดตเบอร์โทรเรียบร้อยแล้ว');
      } else {
        throw new Error('ไม่สามารถอัปเดตเบอร์โทรได้');
      }
    } catch (error) {
      console.error('Error updating phones:', error);
      alert('เกิดข้อผิดพลาดในการอัปเดตเบอร์โทร');
    } finally {
      setIsUpdating(false);
    }
  };

  // ฟังก์ชัน initialize PM service
  const handleInitializePMService = async () => {
    if (!sale || !sale.items) {
      alert('ไม่พบข้อมูลการขายหรือรายการสินค้า');
      return;
    }

    // ตรวจสอบว่ามีแบตเตอรี่ที่ต้องทำ PM service หรือไม่
    const batteryItems = sale.items.filter(item =>
      item.product_type === 'battery' &&
      item.serial_number &&
      item.pm_service_count > 0
    );

    if (batteryItems.length === 0) {
      alert('ไม่พบแบตเตอรี่ที่ต้องทำ PM service ในการขายนี้');
      return;
    }

    const confirmMessage = `คุณต้องการสร้าง PM Service Schedule สำหรับแบตเตอรี่ ${batteryItems.length} ตัวใช่หรือไม่?\n\nรายการแบตเตอรี่:\n${batteryItems.map(item => `- ${item.serial_number} (${item.pm_service_count} ครั้ง)`).join('\n')}`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsInitializingPM(true);
    try {
      const response = await fetch('/api/pm-service/initialize', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sale_id: sale.id,
          customer_name: sale.customer_name,
          customer_phone: sale.customer_phone || editData.customer_phone,
          customer_address: sale.customer_address,
          items: batteryItems.map(item => ({
            serial_number: item.serial_number,
            product_name: item.product_name,
            warranty_period: item.warranty_period,
            pm_service_count: item.pm_service_count,
            sale_date: sale.saleDate || sale.sale_date
          }))
        })
      });

      if (response.ok) {
        const result = await response.json();
        alert(`สร้าง PM Service Schedule เรียบร้อยแล้ว!\n\nสร้างตารางงาน ${result.schedules_created} รายการ\nสำหรับแบตเตอรี่ ${batteryItems.length} ตัว`);

        // รีเฟรชข้อมูล PM stats
        const stats = {};
        for (const item of batteryItems) {
          try {
            const statsResponse = await fetch(`/api/pm-services/stats/serial/${item.serial_number}`);
            if (statsResponse.ok) {
              const data = await statsResponse.json();
              stats[item.serial_number] = data;
            }
          } catch (error) {
            console.error(`Error fetching updated PM stats for ${item.serial_number}:`, error);
          }
        }
        setPmStats(prevStats => ({ ...prevStats, ...stats }));

        if (onUpdate) onUpdate();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'ไม่สามารถสร้าง PM Service Schedule ได้');
      }
    } catch (error) {
      console.error('Error initializing PM service:', error);
      alert(`เกิดข้อผิดพลาดในการสร้าง PM Service Schedule: ${error.message}`);
    } finally {
      setIsInitializingPM(false);
    }
  };

  // ฟังก์ชันลบรายการขายแบบ Force (พร้อมลบ PM schedules & inspections)
  const handleForceDeleteSale = async () => {
    if (!sale || !sale.id) return;

    const confirmMessage = `คุณต้องการลบรายการขายนี้แบบ Force ใช่หรือไม่?\n\nการดำเนินการนี้จะ:\n- ลบรายการขาย\n- ลบ PM Schedule ที่เชื่อมโยง\n- ลบการตรวจสภาพ (Inspection) ที่เกี่ยวข้อง\n\nหมายเหตุ: สถานะใบเสนอราคาจะถูกตั้งกลับเป็น pending`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsDeleting(true);
    try {
      const result = await apiService.forceDeleteSale(sale.id);
      if (result.success) {
        alert('ลบรายการขายและข้อมูลที่เกี่ยวข้องเรียบร้อยแล้ว');
        if (onUpdate) onUpdate();
        onClose();
      } else {
        throw new Error(result.error || 'ไม่สามารถลบรายการขายได้');
      }
    } catch (error) {
      console.error('Error force deleting sale:', error);
      alert('เกิดข้อผิดพลาดในการลบ: ' + error.message);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen) return null;

  // แสดง loading หรือ error state
  if (!sale) {
    return (
      <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">รายละเอียดการขาย</h5>
              <button type="button" className="btn-close" onClick={onClose}></button>
            </div>
            <div className="modal-body text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">กำลังโหลด...</span>
              </div>
              <p className="mt-2">กำลังโหลดข้อมูล...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getProductTypeLabel = (type) => {
    switch (type) {
      case 'battery': return 'แบตเตอรี่';
      case 'charger': return 'ชาร์จเจอร์';
      case 'mixed': return 'ผสม';
      default: return type || 'ไม่ระบุ';
    }
  };

  return (
    <ErrorBoundary onClose={onClose}>
      <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header bg-success text-white">
              <h5 className="modal-title">
                <i className="fas fa-check-circle me-2"></i>
                ปิดการขายสำเร็จ
              </h5>
              <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
            </div>
            <div className="modal-body">


              {/* ข้อมูลทั่วไป */}
              <div className="row mb-4">
                <div className="col-md-6">
                  <h6 className="text-primary"><i className="fas fa-user me-2"></i>ข้อมูลลูกค้า</h6>
                  <div className="bg-light p-3 rounded mb-3">
                    <div><strong>ชื่อ:</strong> {sale.customer_name}</div>
                    {isEditing ? (
                      <>
                        <div className="mb-2">
                          <strong>เบอร์โทร:</strong>
                          <input
                            type="text"
                            className="form-control form-control-sm mt-1"
                            value={editData.customer_phone}
                            onChange={(e) => setEditData({ ...editData, customer_phone: e.target.value })}
                            placeholder="เบอร์โทรหลัก"
                          />
                        </div>
                        <div>
                          <strong>เบอร์โทรสำรอง:</strong>
                          <input
                            type="text"
                            className="form-control form-control-sm mt-1"
                            value={editData.additional_phone}
                            onChange={(e) => setEditData({ ...editData, additional_phone: e.target.value })}
                            placeholder="เบอร์โทรสำรอง (ถ้ามี)"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div><strong>เบอร์โทร:</strong> {sale.customer_phone || 'ไม่มีข้อมูล'}</div>
                        {sale.additional_phone && (
                          <div><strong>เบอร์โทรสำรอง:</strong> {sale.additional_phone}</div>
                        )}
                      </>
                    )}
                    <div><strong>ที่อยู่:</strong> {sale.customer_address || 'ไม่มีข้อมูล'}</div>
                  </div>
                </div>
                <div className="col-md-6">
                  <h6 className="text-primary"><i className="fas fa-file-invoice me-2"></i>ข้อมูลการขาย</h6>
                  <div className="bg-light p-3 rounded mb-3">
                    <div><strong>เลขที่ใบเสนอราคา:</strong> {sale.quotation_number || sale.quotationNumber || `QT-${String(sale.id).padStart(8, '0')}`}</div>
                    <div><strong>ยอดรวม:</strong> {DatabaseService.formatCurrency(sale.saleAmount)}</div>
                    <div><strong>วันที่ปิดการขาย:</strong> {DatabaseService.formatDate(sale.saleDate)}</div>
                    <div><strong>วันที่ส่งมอบ:</strong> {(sale.delivery_date || sale.deliveryDate) ? DatabaseService.formatDate(sale.delivery_date || sale.deliveryDate) : '-'}</div>
                  </div>
                </div>
              </div>
              {/* ตารางรายละเอียดสินค้า */}
              <h6 className="text-primary"><i className="fas fa-list me-2"></i>รายละเอียดสินค้า</h6>
              <div className="table-responsive mb-3">
                <table className="table table-striped table-bordered">
                  <thead className="table-dark">
                    <tr>
                      <th>ประเภท</th>
                      <th>รายละเอียด</th>
                      <th>ซีเรียล</th>
                      <th>จำนวน</th>
                      <th>หน่วย</th>
                      <th>รับประกัน เดือน</th>
                      <th>วันหมดอายุ</th>
                      <th>PM Service</th>
                      <th>สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sale.items && sale.items.length > 0 ? (
                      sale.items.map((item, idx) => {
                        // คำนวณวันหมดอายุการรับประกัน
                        let warrantyExpiry = null;
                        let warrantyStatus = 'ไม่มีประกัน';
                        let statusClass = 'text-muted';

                        if (item.warranty_period) {
                          const baseDateStr = (sale.delivery_date || sale.deliveryDate || sale.saleDate);
                          if (baseDateStr) {
                            const baseDate = new Date(baseDateStr);
                            const expiryDate = new Date(baseDate);
                            expiryDate.setMonth(expiryDate.getMonth() + parseInt(item.warranty_period));
                            warrantyExpiry = expiryDate;
                          }

                          const today = new Date();
                          if (warrantyExpiry > today) {
                            warrantyStatus = 'อยู่ในประกัน';
                            statusClass = 'text-success';
                          } else {
                            warrantyStatus = 'หมดประกัน';
                            statusClass = 'text-danger';
                          }
                        }

                        return (
                          <tr key={idx}>
                            <td>{getProductTypeLabel(item.product_type)}</td>
                            <td>{item.product_name || '-'}</td>
                            <td>
                              <span className="badge bg-primary">
                                {item.serial_number || '-'}
                              </span>
                            </td>
                            <td>{item.quantity || 1}</td>
                            <td>ชิ้น</td>
                            <td className="text-center">
                              {item.warranty_period ? (
                                <span className="badge bg-info">
                                  {item.warranty_period}
                                </span>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {warrantyExpiry ? (
                                DatabaseService.formatDate(warrantyExpiry.toISOString().split('T')[0])
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td className="text-center">
                              {item.pm_service_count !== undefined && item.pm_service_count !== null && item.pm_service_count > 0 ? (
                                (() => {
                                  const stats = pmStats[item.serial_number];
                                  const completed = stats ? stats.completed_services : 0;
                                  const total = item.pm_service_count;
                                  const isComplete = completed >= total;

                                  return (
                                    <div>
                                      <span className={`badge ${isComplete ? 'bg-success' : 'bg-warning text-dark'}`}>
                                        {completed}/{total}
                                      </span>
                                      {isComplete && (
                                        <div className="mt-1">
                                          <small className="text-success fw-bold">PM ครบ</small>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              <span className={`fw-bold ${statusClass}`}>
                                {warrantyStatus}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="9" className="text-center text-muted">
                          ไม่มีข้อมูลสินค้า
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>


            </div>
            <div className="modal-footer">
              {isEditing ? (
                <>
                  <button
                    type="button"
                    className="btn btn-success me-2"
                    onClick={handleSavePhones}
                    disabled={isUpdating}
                  >
                    {isUpdating ? 'กำลังบันทึก...' : 'บันทึก'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setIsEditing(false);
                      setEditData({
                        customer_phone: sale?.customer_phone || '',
                        additional_phone: sale?.additional_phone || ''
                      });
                    }}
                    disabled={isUpdating}
                  >
                    ยกเลิก
                  </button>
                </>
              ) : (
                <>

                  <button
                    type="button"
                    className="btn btn-warning me-2"
                    onClick={() => setIsEditing(true)}
                  >
                    เบอร์โทรสำรอง
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onClose}
                  >
                    ปิด
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default SaleDetailsModal;